import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    FormProvider,
    useForm,
    useFormContext,
    Controller,
} from "react-hook-form";

import omit from "lodash/omit.js";

import Box from "@/ui/atoms/Box";
import Dropdown from "@/ui/molecules/Dropdown";

import type { DealNameCheck } from "@/ui/organisms/dealTypes";

import { debounceWithPromise } from "@/utils/debounce";
import { isTruthy } from "@/utils/guards";
import Skeleton from "@/ui/atoms/Skeleton";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import TextField from "@/ui/atoms/TextField";
import { FieldLabel, Tooltip } from "@proda-ai/ui-kit";
import TextArea from "@/ui/atoms/TextArea";

export const testIds = {
    nameWarningMessage: "DealInfoForm-nameWarningMessage",
};
interface DealInfoFormData {
    dealName: string;
    description?: string | null;
    coaId: number;
}

export type DealInfoFormProps = {
    initialData: Partial<DealInfoFormData>;
    databaseId: number;
    disableCoAsDropdown?: boolean;
    disabledCoAsDropdownTooltip?: string;
    availableCoAs: Array<{
        coaId: number;
        name: string;
    }>;
    availableCoAsStatus: "idle" | "pending" | "resolved" | "rejected";
    onSubmit: (data: DealInfoFormData) => Promise<void>;
    setDealNameCheck: (
        databaseId: number,
        dealName: string,
    ) => Promise<DealNameCheck>;
    // Children allows for a footer that is inside the form.
    children: (props: ChildrenProps) => React.ReactNode;
    requireDirtyForSubmit?: boolean;
};

type ChildrenProps = {
    onSubmit: (event?: React.BaseSyntheticEvent) => Promise<void>;
    isSubmitDisabled: boolean;
};

export type DealInfo = {
    coaId: number;
    dealName: string;
    description: string;
};

export const ERROR_MESSAGES = {
    required: "Project name is required",
    tooLong: (max: number) =>
        `Project name is too long (max ${max} characters)`,
    alreadyExists: "Project name already exists",
    similar: (matches: string[]) => {
        const numMatches = matches.length;
        const matchesJoined =
            numMatches === 1
                ? matches[0]
                : matches.slice(0, -1).join(", ") +
                  " and " +
                  matches[numMatches - 1];
        return `Project name is similar to ${matchesJoined}`;
    },
    descriptionTooLong: (max: number) =>
        `Description is too long (max ${max} characters)`,
    failedToCheckDealName: "There was an error checking the project name",
};

export const DealInfoForm: React.FC<DealInfoFormProps> = ({
    initialData,
    databaseId,
    availableCoAs,
    availableCoAsStatus,
    disableCoAsDropdown = false,
    disabledCoAsDropdownTooltip,
    setDealNameCheck,
    children,
    onSubmit,
    requireDirtyForSubmit = true,
}) => {
    const methods = useForm<DealInfo>({
        defaultValues:
            initialData.coaId !== null
                ? {
                      coaId: initialData.coaId,
                      dealName: initialData.dealName ?? "",
                      description: initialData.description ?? "",
                  }
                : {
                      dealName: initialData.dealName ?? "",
                      description: initialData.description ?? "",
                  },
        mode: "all",
    });

    const {
        setValue,
        reset,
        handleSubmit,
        watch,
        trigger,
        formState: { isValid, isDirty },
    } = methods;

    // Track submission state
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dealName = watch("dealName");

    // Trigger validation for dealName on mount if non-empty
    useEffect(() => {
        if (isTruthy(dealName)) {
            void trigger("dealName");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [warnings, setWarnings] = React.useState<
        Record<string, { message: string }>
    >({});

    const setDealNameCheckRef = React.useRef(setDealNameCheck);
    useEffect(() => {
        setDealNameCheckRef.current = setDealNameCheck;
    }, [setDealNameCheck]);

    const onTrimSubmit = useCallback(
        async (data: DealInfo) => {
            try {
                setIsSubmitting(true);
                const trimmedData = {
                    dealName: data.dealName.trim(),
                    description: data.description.trim(),
                    coaId: data.coaId,
                };
                await onSubmit(trimmedData);
                // Reset form state to mark as pristine after successful submission
                reset({
                    coaId: data.coaId,
                    dealName: data.dealName.trim(),
                    description: data.description.trim(),
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [onSubmit, reset],
    );

    const clearDealNameWarnings = useCallback(() => {
        setWarnings(warnings => omit(warnings, "dealName"));
    }, []);
    const setDealNameWarning = useCallback((message: string) => {
        setWarnings(warnings => ({
            ...warnings,
            dealName: { message },
        }));
    }, []);

    const [isValidatingDealName, setIsValidatingDealName] = useState(false);
    const initialDataDealName = initialData.dealName?.trim() ?? "";
    const validateDealName = useCallback(
        async (dealName: string) => {
            setIsValidatingDealName(true);
            try {
                const dealNameTrimmed = dealName.trim();
                if (!dealNameTrimmed) {
                    return ERROR_MESSAGES.required;
                }
                if (dealNameTrimmed.length > 100) {
                    return ERROR_MESSAGES.tooLong(100);
                }
                const dealNameCheckState = await setDealNameCheckRef.current(
                    databaseId,
                    dealNameTrimmed,
                );

                if (dealNameCheckState.success === false) {
                    return ERROR_MESSAGES.failedToCheckDealName;
                }
                if (
                    dealNameCheckState.isExactMatch &&
                    dealName.trim() !== initialDataDealName
                ) {
                    return ERROR_MESSAGES.alreadyExists;
                }

                const closeMatches = dealNameCheckState.closeMatches.filter(
                    match => match !== initialDataDealName,
                );
                if (closeMatches.length > 0) {
                    const message = ERROR_MESSAGES.similar(closeMatches);
                    setDealNameWarning(message);
                } else {
                    clearDealNameWarnings();
                }

                return true;
            } catch {
                return ERROR_MESSAGES.failedToCheckDealName;
            } finally {
                setIsValidatingDealName(false);
            }
        },
        [
            clearDealNameWarnings,
            databaseId,
            initialDataDealName,
            setDealNameWarning,
        ],
    );

    const validateDealNameDebounced = useMemo(
        // A cancelled run resolves to "valid"; RHF can't handle rejections.
        () =>
            debounceWithPromise(
                validateDealName,
                350,
                {},
                { resolveWith: true },
            ),
        [validateDealName],
    );
    useEffect(
        () => validateDealNameDebounced.cancel,
        [validateDealNameDebounced],
    );

    const areCoAsAvailable = availableCoAsStatus === "resolved";
    const displayCoAsDropdown = areCoAsAvailable;
    const displayCoAsError =
        availableCoAsStatus === "rejected" && !areCoAsAvailable;
    const showDisabledTooltip =
        disableCoAsDropdown &&
        isTruthy(disabledCoAsDropdownTooltip) &&
        disabledCoAsDropdownTooltip.length > 0;
    // Auto-select the CoA only when exactly one is available — there is no
    // choice to make. When multiple exist we don't guess; the user must choose.
    const soleCoAId =
        availableCoAs.length === 1 ? (availableCoAs[0]?.coaId as number) : null;

    React.useEffect(() => {
        if (soleCoAId === null) return;
        if (initialData.coaId !== null && initialData.coaId !== undefined)
            return;
        setValue("coaId", soleCoAId, { shouldValidate: true });
    }, [soleCoAId, initialData.coaId, setValue]);

    const canSubmit =
        isValid &&
        !isSubmitting &&
        !isValidatingDealName &&
        (isDirty || !requireDirtyForSubmit);
    return (
        <FormProvider {...methods}>
            <Box sx={{ width: "500px", margin: "2rem auto" }}>
                <form
                    onSubmit={e => {
                        // Prevent submission when disabled
                        if (!canSubmit) {
                            e.preventDefault();
                            return;
                        }
                        void handleSubmit(onTrimSubmit)(e);
                    }}
                >
                    {displayCoAsError && (
                        <ErrorMessage
                            sx={{
                                width: "100%",
                                marginBottom: "1rem",
                            }}
                        >
                            Error loading chart of accounts, try reloading page.
                        </ErrorMessage>
                    )}
                    <DealNameInput
                        validateDealNameDebounced={validateDealNameDebounced}
                        warnings={warnings}
                        isLoading={!areCoAsAvailable}
                    />
                    <DescriptionInput isLoading={!areCoAsAvailable} />
                    {displayCoAsDropdown && (
                        <>
                            <FieldLabel
                                fieldId="coaId"
                                label="Chart of Accounts"
                                mb={1}
                            />
                            <Controller
                                control={methods.control}
                                name="coaId"
                                rules={{
                                    required:
                                        "Selecting chart of accounts is required",
                                }}
                                render={({ field, formState: { errors } }) => {
                                    const dropdown = (
                                        <Dropdown
                                            hideLabel={true}
                                            disabled={disableCoAsDropdown}
                                            options={availableCoAs.map(coa => ({
                                                label: coa.name,
                                                value: coa.coaId,
                                            }))}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    );
                                    return (
                                        <>
                                            {showDisabledTooltip ? (
                                                <Tooltip
                                                    content={
                                                        disabledCoAsDropdownTooltip
                                                    }
                                                    style={{
                                                        display: "block",
                                                        width: "100%",
                                                    }}
                                                >
                                                    <Box sx={{ width: "100%" }}>
                                                        {dropdown}
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                dropdown
                                            )}
                                            {errors.coaId &&
                                                errors.coaId.message !==
                                                    undefined && (
                                                    <FieldLabel
                                                        mt={1}
                                                        fieldId="coaIdError"
                                                        label={
                                                            errors.coaId.message
                                                        }
                                                        hasErrors={true}
                                                    />
                                                )}
                                        </>
                                    );
                                }}
                            />
                        </>
                    )}
                </form>
            </Box>
            <Box
                sx={{
                    padding: "2rem",
                    borderTop: "1px solid var(--proda-white-150)",
                    marginTop: "auto",
                }}
            >
                {children({
                    onSubmit: async event => {
                        if (!canSubmit) {
                            event?.preventDefault();
                            return;
                        }
                        await handleSubmit(onTrimSubmit)(event);
                    },
                    isSubmitDisabled: !canSubmit,
                })}
            </Box>
        </FormProvider>
    );
};

const DealNameInput: React.FC<{
    validateDealNameDebounced: (value: string) => Promise<true | string>;
    warnings: Record<string, { message: string }>;
    isLoading: boolean;
}> = ({ validateDealNameDebounced, warnings, isLoading }) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<DealInfo>();

    return (
        <Box sx={{ marginBottom: "1rem" }}>
            <FieldLabel fieldId="field-dealName" label="Project Name" mb={1} />
            {isLoading ? (
                <Skeleton variant="text" sx={{ height: "32px" }} />
            ) : (
                <>
                    <TextField
                        id="dealName"
                        {...register("dealName", {
                            validate: validateDealNameDebounced,
                        })}
                        placeholder="Project Name"
                        hasErrors={!!errors.dealName}
                        errorMessage={errors.dealName?.message}
                    />
                    {!errors.dealName &&
                    warnings["dealName"] &&
                    warnings["dealName"].message !== undefined ? (
                        <Box
                            component={FieldLabel}
                            fieldId="dealNameWarning"
                            data-testid={testIds.nameWarningMessage}
                            label={warnings["dealName"].message}
                            mt={1}
                            sx={{
                                "& label": {
                                    color: "var(--proda-yellow)",
                                },
                            }}
                        />
                    ) : null}
                </>
            )}
        </Box>
    );
};

const DescriptionInput: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<DealInfo>();

    return (
        <Box sx={{ marginBottom: "0.5rem" }}>
            <FieldLabel
                fieldId="field-description"
                label="Description"
                mb={1}
            />
            {isLoading ? (
                <Skeleton variant="text" sx={{ height: "100px" }} />
            ) : (
                <>
                    <TextArea
                        id="description"
                        {...register("description", {
                            maxLength: {
                                value: 3000,
                                message:
                                    ERROR_MESSAGES.descriptionTooLong(3000),
                            },
                        })}
                        placeholder="Description"
                        sx={{
                            minHeight: "100px",
                            width: "100%",
                            resize: "vertical",
                        }}
                        hasErrors={!!errors.description}
                        errorMessage={errors.description?.message}
                    />
                </>
            )}
        </Box>
    );
};
